import * as Y from 'yjs';
import type { TipTapNode } from '../tiptap/types.js';

export function decodeAnnouncementBody(body: unknown): TipTapNode | null {
  if (body == null) return null;
  if (typeof body === 'object') return body as TipTapNode;
  if (typeof body !== 'string') return null;

  const trimmed = body.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('<')) {
    try {
      return JSON.parse(trimmed) as TipTapNode;
    } catch {
      return { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: trimmed }] }] };
    }
  }

  try {
    const binary = Uint8Array.from(atob(trimmed), (c) => c.charCodeAt(0));
    const ydoc = new Y.Doc();
    Y.applyUpdate(ydoc, binary);
    const fragment = ydoc.getXmlFragment('default');
    const content = fragmentToNodes(fragment);
    ydoc.destroy();
    return { type: 'doc', content };
  } catch {
    return null;
  }
}

function fragmentToNodes(fragment: Y.XmlFragment): TipTapNode[] {
  const nodes: TipTapNode[] = [];
  for (let index = 0; index < fragment.length; index += 1) {
    const child = fragment.get(index);
    if (child instanceof Y.XmlElement) {
      nodes.push(elementToNode(child));
    } else if (child instanceof Y.XmlText) {
      const inline = deltaToInline(child.toDelta());
      if (inline.length > 0) nodes.push({ type: 'paragraph', content: inline });
    }
  }
  return nodes;
}

function elementToNode(element: Y.XmlElement): TipTapNode {
  const rawAttrs = element.getAttributes();
  const attrs: Record<string, unknown> = {};
  for (const key of Object.keys(rawAttrs)) attrs[key] = rawAttrs[key];

  const content: TipTapNode[] = [];
  for (let index = 0; index < element.length; index += 1) {
    const child = element.get(index);
    if (child instanceof Y.XmlElement) content.push(elementToNode(child));
    else if (child instanceof Y.XmlText) content.push(...deltaToInline(child.toDelta()));
  }

  const node: TipTapNode = { type: element.nodeName };
  if (Object.keys(attrs).length > 0) node.attrs = attrs;
  if (content.length > 0) node.content = content;
  return node;
}

interface Delta {
  insert?: unknown;
  attributes?: Record<string, unknown>;
}

function deltaToInline(delta: Delta[]): TipTapNode[] {
  const nodes: TipTapNode[] = [];
  for (const op of delta) {
    if (typeof op.insert !== 'string') continue;
    const node: TipTapNode = { type: 'text', text: op.insert };
    if (op.attributes) {
      const marks: { type: string; attrs?: Record<string, unknown> }[] = [];
      for (const [type, attrs] of Object.entries(op.attributes)) {
        if (!attrs) continue;
        marks.push(typeof attrs === 'object' ? { type, attrs: attrs as Record<string, unknown> } : { type });
      }
      if (marks.length > 0) node.marks = marks;
    }
    nodes.push(node);
  }
  return nodes;
}
